import { ArrowRight } from 'lucide-react';
import type { BookingResponse } from '@/shared/api';
import { formatBookingPrice } from '@/shared/lib/bookingPrice';
import { earlyDeparturePlanLabel } from './earlyDeparturePresentation';
import { formatOccupancy } from '@/shared/lib/occupancy';
import { calcNights, channelName, isOta, stayDate } from './helpers';

interface Props {
  booking: BookingResponse;
  propertyName: string | null;
  canSeeGuests: boolean;
  /** Nur Verwalter: springt in den Reiter Geld. */
  onOpenMoney?: () => void;
}

const card = 'border-hairline rounded-tile flex flex-col gap-2.5 border bg-white p-5';
const label = 'text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.16em]';

/** Linke Spalte (Board „Buchung“): Aufenthalt in Zeilen, darunter der Preis. */
export function BookingStayAside({ booking, propertyName, canSeeGuests, onOpenMoney }: Props) {
  const nights = calcNights(booking.checkInDate, booking.checkOutDate);
  const plan = booking.earlyDeparturePlan;
  const rows: Array<[string, string]> = [
    [
      'Anreise',
      `${stayDate(booking.checkInDate, booking.objectToday)}${booking.arrivalHour ? ` · ca. ${booking.arrivalHour.slice(0, 5)}` : ''}`,
    ],
    ['Abreise', stayDate(booking.checkOutDate, booking.objectToday)],
    ...(booking.actualDepartureDate
      ? [
          ['Tatsächlich abgereist', stayDate(booking.actualDepartureDate, booking.objectToday)] as [
            string,
            string,
          ],
        ]
      : []),
    ...(plan
      ? [
          [
            `Geplante Abreise · ${earlyDeparturePlanLabel(plan.status)}`,
            stayDate(plan.plannedDepartureDate, booking.objectToday),
          ] as [string, string],
        ]
      : []),
    ['Nächte', String(nights)],
    ['Gäste', canSeeGuests ? formatOccupancy(booking) : `${booking.guestCount}`],
    ['Apartment', propertyName ?? '–'],
  ];
  return (
    <div className="flex flex-col gap-4">
      <section aria-labelledby="stay-aside" className={card}>
        <h2 id="stay-aside" className={label}>
          Aufenthalt
        </h2>
        <dl className="flex flex-col gap-2 text-sm">
          {rows.map(([term, value]) => (
            <div key={term} className="flex items-baseline justify-between gap-3">
              <dt className="text-ink-soft">{term}</dt>
              <dd className="text-right font-semibold">{value}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section aria-labelledby="money-aside" className={card}>
        <h2 id="money-aside" className={label}>
          Geld
        </h2>
        <p className="font-serif text-[28px] font-bold leading-none">
          {formatBookingPrice(booking)}
        </p>
        <p className="text-ink-soft text-[13px]">
          Buchungspreis · {isOta(booking) ? `über ${channelName(booking.channel)}` : 'direkt'} ·{' '}
          {nights} {nights === 1 ? 'Nacht' : 'Nächte'}
        </p>
        {onOpenMoney && (
          <button
            type="button"
            onClick={onOpenMoney}
            className="text-forest flex min-h-11 items-center gap-1.5 self-start text-[13px] font-bold"
          >
            Zu Geld <ArrowRight size={14} aria-hidden />
          </button>
        )}
      </section>
    </div>
  );
}
